import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Plus, Trash2, X, Crown, Eye, EyeOff, Star, ArrowUp, ArrowDown, ListChecks } from "lucide-react";

const EMPTY_PLAN = {
  side: "parent",
  name: "",
  tag: "",
  amount: 0,
  currency: "INR",
  price_display: "",
  unit_display: "/month",
  interval_days: 30,
  trial_days: 0,
  tagline: "",
  cta_label: "Subscribe",
  featured: false,
  display_order: 100,
  is_public: true,
  features: [],
  razorpay_plan_id: "",
  total_cycles: 120,
};

export default function AdminPlans() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => axios.get(`${API}/admin/plans`).then(r => setItems(r.data.plans)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Delete plan "${p.name}"? Subscribers on this plan keep access until expiry.`)) return;
    try {
      await axios.delete(`${API}/admin/plans/${p.plan_id}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not delete plan");
    }
  };

  const togglePublic = async (p) => {
    await axios.patch(`${API}/admin/plans/${p.plan_id}`, { ...p, is_public: !p.is_public });
    load();
  };

  const toggleFeatured = async (p) => {
    // Only one featured per side at a time — clear others on this side first
    if (!p.featured) {
      const others = items.filter(x => x.side === p.side && x.featured && x.plan_id !== p.plan_id);
      for (const o of others) {
        await axios.patch(`${API}/admin/plans/${o.plan_id}`, { ...o, featured: false });
      }
    }
    await axios.patch(`${API}/admin/plans/${p.plan_id}`, { ...p, featured: !p.featured });
    load();
  };

  const reorder = async (p, delta) => {
    await axios.patch(`${API}/admin/plans/${p.plan_id}`, { ...p, display_order: (p.display_order || 100) + delta });
    load();
  };

  const parent = items.filter(p => p.side === "parent");
  const vendor = items.filter(p => p.side === "vendor");

  return (
    <div className="space-y-8" data-testid="admin-plans-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold inline-flex items-center gap-1"><ListChecks className="w-4 h-4"/> Subscription plans</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">Pricing &amp; plan catalogue</h1>
          <p className="text-ink-soft text-sm max-w-2xl">Edits here propagate everywhere — landing page, /app/billing, vendor billing. Mark a plan <strong className="text-ink">public</strong> to surface it on the landing page; <strong className="text-ink">featured</strong> plans get the highlighted middle card.</p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="add-plan-btn" className="bg-brand text-white rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5">
          <Plus className="w-4 h-4"/> New plan
        </button>
      </header>

      <PlanGroup title="Parent (pet owner) plans" plans={parent} onEdit={setEditing} onDelete={remove} onTogglePublic={togglePublic} onToggleFeatured={toggleFeatured} onReorder={reorder}/>
      <PlanGroup title="Vendor (vet · store · hostel · NGO) plans" plans={vendor} onEdit={setEditing} onDelete={remove} onTogglePublic={togglePublic} onToggleFeatured={toggleFeatured} onReorder={reorder}/>

      {creating && <PlanModal onClose={() => { setCreating(false); load(); }}/>}
      {editing && <PlanModal plan={editing} onClose={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function PlanGroup({ title, plans, onEdit, onDelete, onTogglePublic, onToggleFeatured, onReorder }) {
  if (plans.length === 0) return (
    <section>
      <h2 className="font-heading text-xl font-bold mb-3">{title}</h2>
      <div className="bg-surface border border-dashed border-line rounded-3xl p-6 text-center text-ink-soft text-sm">No plans yet.</div>
    </section>
  );
  return (
    <section data-testid={`plan-group-${plans[0].side}`}>
      <h2 className="font-heading text-xl font-bold mb-3">{title}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.map(p => (
          <article key={p.plan_id} data-testid={`plan-${p.plan_id}`} className={`bg-surface border rounded-3xl p-5 shadow-soft transition-all ${p.featured ? "border-brand ring-2 ring-brand/20" : "border-line"} ${!p.is_public ? "opacity-60" : ""}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-brand">{p.tag || p.side}</div>
              <div className="flex items-center gap-1">
                {p.featured && <Star className="w-3.5 h-3.5 text-brand fill-brand" title="Featured"/>}
                {!p.is_public && <EyeOff className="w-3.5 h-3.5 text-ink-soft" title="Hidden from landing"/>}
              </div>
            </div>
            <h3 className="font-heading text-xl font-extrabold mt-1">{p.name}</h3>
            <div className="mt-2 flex items-baseline gap-1.5">
              <span className="font-heading text-3xl font-extrabold">{p.price_display || `₹${p.amount}`}</span>
              <span className="text-xs font-bold text-ink-soft">{p.unit_display || ""}</span>
            </div>
            {p.tagline && <p className="text-xs text-ink-soft mt-1.5 line-clamp-2">{p.tagline}</p>}
            {p.trial_days > 0 && <div className="mt-2 inline-block bg-leaf/30 text-ink text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">{p.trial_days}-day trial</div>}
            <ul className="mt-3 space-y-1 text-xs">
              {(p.features || []).slice(0, 4).map((f, i) => (
                <li key={i} className="text-ink-soft truncate">• {f}</li>
              ))}
              {(p.features || []).length > 4 && <li className="text-ink-soft text-[10px]">+{p.features.length - 4} more</li>}
            </ul>
            <div className="flex items-center gap-1 mt-4 border-t border-line pt-3">
              <button onClick={() => onReorder(p, -10)} title="Move up" className="text-ink-soft hover:text-brand p-1.5"><ArrowUp className="w-3.5 h-3.5"/></button>
              <button onClick={() => onReorder(p, 10)} title="Move down" className="text-ink-soft hover:text-brand p-1.5"><ArrowDown className="w-3.5 h-3.5"/></button>
              <button onClick={() => onToggleFeatured(p)} title={p.featured ? "Unset featured" : "Mark featured"} className={`p-1.5 ${p.featured ? "text-brand" : "text-ink-soft hover:text-brand"}`} data-testid={`featured-${p.plan_id}`}>
                <Star className={`w-3.5 h-3.5 ${p.featured ? "fill-brand" : ""}`}/>
              </button>
              <button onClick={() => onTogglePublic(p)} title={p.is_public ? "Hide from landing" : "Show on landing"} className="text-ink-soft hover:text-brand p-1.5" data-testid={`public-${p.plan_id}`}>
                {p.is_public ? <Eye className="w-3.5 h-3.5"/> : <EyeOff className="w-3.5 h-3.5"/>}
              </button>
              <button onClick={() => onEdit(p)} className="ml-auto text-brand text-xs font-bold" data-testid={`edit-${p.plan_id}`}>Edit</button>
              <button onClick={() => onDelete(p)} title="Delete" className="text-ink-soft hover:text-danger p-1.5" data-testid={`delete-${p.plan_id}`}>
                <Trash2 className="w-3.5 h-3.5"/>
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function PlanModal({ plan, onClose }) {
  const isEdit = !!plan;
  const [form, setForm] = useState(plan ? { ...plan, features: (plan.features || []).join("\n") } : { ...EMPTY_PLAN, features: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const payload = {
        ...form,
        amount: Number(form.amount) || 0,
        interval_days: Number(form.interval_days) || 0,
        trial_days: Number(form.trial_days) || 0,
        display_order: Number(form.display_order) || 100,
        total_cycles: Number(form.total_cycles) || 120,
        features: String(form.features || "").split("\n").map(s => s.trim()).filter(Boolean),
      };
      if (isEdit) {
        await axios.patch(`${API}/admin/plans/${plan.plan_id}`, payload);
      } else {
        await axios.post(`${API}/admin/plans`, payload);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save plan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-lg border border-line shadow-soft max-h-[90vh] overflow-y-auto" data-testid="plan-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-extrabold inline-flex items-center gap-2"><Crown className="w-5 h-5 text-brand"/> {isEdit ? "Edit plan" : "New plan"}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5"/></button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Plan name *">
            <input required data-testid="plan-name" value={form.name} onChange={e => set("name", e.target.value)} className="inp"/>
          </Field>
          <Field label="Side">
            <select value={form.side} onChange={e => set("side", e.target.value)} className="inp" disabled={isEdit}>
              <option value="parent">Parent (pet owner)</option>
              <option value="vendor">Vendor (business)</option>
            </select>
          </Field>
          <Field label="Tag (e.g. Free / Premium / Yearly)">
            <input value={form.tag || ""} onChange={e => set("tag", e.target.value)} className="inp"/>
          </Field>
          <Field label="Tagline (one-liner)">
            <input value={form.tagline || ""} onChange={e => set("tagline", e.target.value)} className="inp"/>
          </Field>
          <Field label="Amount (integer, in plan currency)">
            <input type="number" value={form.amount} onChange={e => set("amount", e.target.value)} className="inp"/>
          </Field>
          <Field label="Currency">
            <select value={form.currency} onChange={e => set("currency", e.target.value)} className="inp">
              <option value="INR">INR (₹)</option>
              <option value="USD">USD ($)</option>
            </select>
          </Field>
          <Field label="Price display (overrides auto)">
            <input value={form.price_display || ""} onChange={e => set("price_display", e.target.value)} placeholder="₹299" className="inp"/>
          </Field>
          <Field label="Unit display">
            <input value={form.unit_display || ""} onChange={e => set("unit_display", e.target.value)} placeholder="/month" className="inp"/>
          </Field>
          <Field label="Interval (days) — 30 / 365 / 0 for one-time">
            <input type="number" value={form.interval_days} onChange={e => set("interval_days", e.target.value)} className="inp"/>
          </Field>
          <Field label="Trial days (0 = no trial)">
            <input type="number" value={form.trial_days} onChange={e => set("trial_days", e.target.value)} className="inp"/>
          </Field>
          <Field label="CTA label">
            <input value={form.cta_label || ""} onChange={e => set("cta_label", e.target.value)} placeholder="Subscribe" className="inp"/>
          </Field>
          <Field label="Display order (lower = first)">
            <input type="number" value={form.display_order} onChange={e => set("display_order", e.target.value)} className="inp"/>
          </Field>
        </div>

        <Field label="Features (one per line)">
          <textarea data-testid="plan-features" value={form.features} onChange={e => set("features", e.target.value)} rows={6} placeholder="AI 24/7 unlimited&#10;Priority vet booking&#10;Unlimited Rx history" className="inp"/>
        </Field>

        <div className="mt-5 p-4 rounded-2xl bg-sky/10 border border-sky/40">
          <div className="text-xs uppercase tracking-wider font-bold text-sky mb-1">Razorpay auto-renew (Phase 3)</div>
          <p className="text-[11px] text-ink-soft mb-3">Paste the <code className="bg-surface-muted rounded px-1 font-mono text-[10px]">plan_…</code> ID created in your <a href="https://dashboard.razorpay.com/app/subscriptions/plans" target="_blank" rel="noreferrer" className="text-sky underline font-bold">Razorpay Dashboard → Subscriptions → Plans</a>. When set, users get a real UPI / card mandate at checkout (auto-charges on renewal).</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Razorpay Plan ID">
              <input data-testid="plan-rzp-id" value={form.razorpay_plan_id || ""} onChange={e => set("razorpay_plan_id", e.target.value)} placeholder="plan_LSjwH5u4j2pX1Q" className="inp"/>
            </Field>
            <Field label="Total billing cycles (max 120)">
              <input type="number" min={1} max={120} value={form.total_cycles || 120} onChange={e => set("total_cycles", e.target.value)} className="inp"/>
            </Field>
          </div>
        </div>

        <div className="flex items-center gap-5 mt-3 text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_public} onChange={e => set("is_public", e.target.checked)} data-testid="plan-public"/>
            <span><strong>Public</strong> · show on landing page</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.featured} onChange={e => set("featured", e.target.checked)} data-testid="plan-featured"/>
            <span><strong>Featured</strong> · highlight as best</span>
          </label>
        </div>

        {error && <p className="text-sm text-danger mt-3 bg-danger/10 rounded-2xl p-3" data-testid="plan-error">{error}</p>}
        <button data-testid="save-plan" disabled={saving} className="mt-5 w-full bg-brand text-white rounded-full py-3 font-bold disabled:opacity-60">{saving ? "Saving…" : (isEdit ? "Save changes" : "Create plan")}</button>
        <style>{`.inp{width:100%;border:1px solid #EAE0D5;border-radius:14px;padding:0.55rem 0.9rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;margin-top:0.25rem;font-size:0.875rem;}.inp:focus{border-color:#FF6B35;}.inp:disabled{opacity:0.6;cursor:not-allowed;}`}</style>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block mt-3">
    <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">{label}</span>
    {children}
  </label>
);
